import { format, isValid, parseISO } from "date-fns";
import { hoursSpanned, orgDateStr, orgTimeInputValue, orgTimeStr } from "./org-time";

/**
 * Backend dates are plain YYYY-MM-DD strings; timestamps are ISO instants.
 *
 * This one is for the date strings — no timezone conversion, because there is no
 * instant to convert. Anything holding a real timestamp goes through formatTime
 * or orgDateStr instead, both of which are pinned to the org's clock.
 */
export function formatDate(value: string | null | undefined, pattern = "d MMM yyyy") {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, pattern) : null;
}

/**
 * The CALENDAR DATE of an ISO timestamp, in the org's timezone.
 *
 * The counterpart to formatTime, and the thing to reach for whenever a
 * timestamptz column — decided_at, created_at, uploaded_at, updated_at — has to
 * be shown as a day. Slicing the first ten characters off the ISO string instead
 * takes the UTC date: a leave approved at 02:00 IST is stored 20:30Z on the
 * previous day and reads as the day before it happened. Everything after 18:30
 * IST is in that window.
 *
 * Plain YYYY-MM-DD columns (start_date, attendance.date, holidays) have no
 * instant to convert and belong in formatDate directly.
 */
export function formatTimestampDate(value: string | null | undefined, pattern = "d MMM yyyy") {
  if (!value) return null;
  return formatDate(orgDateStr(value), pattern);
}

/**
 * Renders an ISO timestamp as a wall-clock time in the ORG's timezone, e.g.
 * "9:42 am" — not the viewer's. A check-in recorded at 9:42 IST has to read as
 * 9:42 for everyone looking at it, including an HR user whose laptop is set to
 * another zone (or simply set wrong). See lib/org-time.ts.
 */
export function formatTime(value: string | null | undefined) {
  if (!value) return null;
  return orgTimeStr(value);
}

/**
 * Renders a Date as a ticking 12-hour clock with seconds, e.g. "9:42:07 am".
 *
 * Also org time, and for a sharper reason than the rest: this is the clock on the
 * check-in card, sitting inches from the button. If it showed the browser's time
 * while the backend stamped IST, the card would promise one time and record
 * another — which is precisely how "the logged time isn't what it said" starts.
 */
export function formatClock(value: Date) {
  return orgTimeStr(value, true) ?? "";
}

/**
 * Renders an ISO timestamp as an HH:MM value for a <input type="time">, in the
 * org's fixed timezone (not the viewer's browser timezone) — so it round-trips
 * correctly with the backend's wallTimeToIso() regardless of what machine the
 * manager/HR user editing the record happens to be on.
 */
export function formatTimeInputValue(value: string | null | undefined): string {
  if (!value) return "";
  return orgTimeInputValue(value);
}

/** Hours between two ISO timestamps, rounded to one decimal. */
export function hoursBetween(from: string | null, to: string | null): number | null {
  const hours = hoursSpanned(from, to);
  if (hours === null || hours <= 0) return null;
  return Math.round(hours * 10) / 10;
}

/** Compact figures for stat tiles: 1,284 / 12.9K / 1.2M. */
export function compactNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${trimZero(value / 1_000_000)}M`;
  if (Math.abs(value) >= 10_000) return `${trimZero(value / 1000)}K`;
  return value.toLocaleString("en-IN");
}

function trimZero(value: number) {
  return value.toFixed(1).replace(/\.0$/, "");
}

/** Leave-day counts come back as halves (0.5 CL). Render them without noise. */
export function formatDays(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Turns snake_case / camelCase API enums into readable labels. */
export function humanize(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());
}
